import { cloneJsonObject, type JsonObject, type JsonValue } from './vega-lite-json';
import { VEGA_LITE_DATASET_NAME } from './vega-lite-widget-options';

/**
 * Row contract shared by the renderer, the data inspector and the evaluation adapter. Values stay
 * primitive so a prepared row can be handed to Vega without exposing query-cache objects.
 */
export type VegaLiteDataValue = boolean | number | string | null;

export type VegaLiteDataRow = Record<string, VegaLiteDataValue>;

export type VegaLiteColorScheme = 'dark' | 'light';

/**
 * Host-resolved styling. The renderer applies it underneath the definition, so explicit styling in
 * the definition always wins and is identifiable as custom styling.
 */
export type VegaLiteRenderStyle = {
  background: string;
  colorScheme: VegaLiteColorScheme;
  gridColor: string;
  textColor: string;
};

export type VegaLiteChartDimensions = {
  height: number;
  width: number;
};

export type VegaLiteChartExport =
  | { dataUrl: string; format: 'png' }
  | { format: 'svg'; markup: string };

export type VegaLiteChartExportFormat = VegaLiteChartExport['format'];

export type VegaLiteInspectorColumn = {
  field: string;
  label: string;
};

const VEGA_LITE_VIEWPORT_PADDING_PX = 4;
const VEGA_LITE_VERTICAL_CHROME_RESERVE_PX = 56;
const VEGA_LITE_MAX_VERTICAL_CHROME_RATIO = 0.35;
const VEGA_LITE_NARROW_VIEWPORT_PX = 720;
const VEGA_LITE_RIGHT_LEGEND_PADDING_PX = 64;
const VEGA_LITE_LEGEND_CHANNELS = [
  'color',
  'fill',
  'opacity',
  'shape',
  'size',
  'stroke',
  'strokeDash',
  'strokeOpacity',
  'strokeWidth',
];

/**
 * Legend properties that place the guide. A channel that sets one of them has an authored layout,
 * which the runtime keeps on a viewport wide enough to show it.
 */
const VEGA_LITE_LEGEND_LAYOUT_KEYS = ['columns', 'direction', 'legendX', 'legendY', 'orient'];

function isPlainJsonObject(value: JsonValue | undefined): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Vega 6.2.0 floors its initial bin step with minstep, then checks it again when subdividing.
 * The smallest positive number prevents a data-derived span from underflowing that step to zero
 * without changing any representable positive step. The policy's base >= 1.5 then guarantees
 * that multiplying even this subnormal floor makes progress. Authored minstep remains forbidden.
 */
function applyBinStepFloor(definition: JsonObject) {
  const bin = definition.bin;

  if (bin === undefined || bin === null) {
    return;
  }

  const params = isPlainJsonObject(bin) ? bin : {};

  if (params.minstep === undefined) {
    definition.bin = { minstep: Number.MIN_VALUE, ...params };
  }
}

function applyEncodingBinStepFloor(definition: JsonValue | undefined) {
  if (Array.isArray(definition)) {
    definition.forEach(applyEncodingBinStepFloor);
    return;
  }

  if (!isPlainJsonObject(definition)) {
    return;
  }

  const bin = definition.bin;

  if (bin === true || Array.isArray(bin) || (isPlainJsonObject(bin) && !bin.binned)) {
    applyBinStepFloor(definition);
  }

  applyEncodingBinStepFloor(definition.condition);
}

function applyRuntimeBinStepFloor(spec: JsonObject) {
  if (isPlainJsonObject(spec.encoding)) {
    Object.values(spec.encoding).forEach(applyEncodingBinStepFloor);
  }

  if (Array.isArray(spec.transform)) {
    spec.transform.forEach((transform) => {
      if (isPlainJsonObject(transform)) {
        // Unlike field definitions, explicit bin transforms run even with bin: false.
        applyBinStepFloor(transform);
      }
    });
  }

  if (Array.isArray(spec.layer)) {
    spec.layer.forEach((layer) => {
      if (isPlainJsonObject(layer)) {
        applyRuntimeBinStepFloor(layer);
      }
    });
  }
}

function mergeConfigGroup(styleGroup: JsonObject, specGroup: JsonValue | undefined): JsonValue {
  if (!isPlainJsonObject(specGroup)) {
    return specGroup === undefined ? styleGroup : specGroup;
  }

  return { ...styleGroup, ...specGroup };
}

function createStyleConfig(style: VegaLiteRenderStyle): Record<string, JsonObject> {
  return {
    axis: {
      domainColor: style.gridColor,
      gridColor: style.gridColor,
      labelColor: style.textColor,
      tickColor: style.gridColor,
      titleColor: style.textColor,
    },
    header: { labelColor: style.textColor, titleColor: style.textColor },
    legend: { labelColor: style.textColor, titleColor: style.textColor },
    title: { color: style.textColor, subtitleColor: style.textColor },
    view: { stroke: style.gridColor },
  };
}

function getLegendChannels(encoding: JsonValue | undefined) {
  if (!isPlainJsonObject(encoding)) {
    return [];
  }

  return VEGA_LITE_LEGEND_CHANNELS.flatMap((channel) => {
    const definition = encoding[channel];
    return isPlainJsonObject(definition) ? [{ channel, definition, encoding }] : [];
  });
}

function isLegendDisabled(definition: JsonObject) {
  return definition.legend === null || definition.legend === false;
}

function forEachSpecLegendChannel(spec: JsonObject, visit: (definition: JsonObject) => void) {
  const config = isPlainJsonObject(spec.config) ? spec.config : undefined;
  const disabledByDefault = isPlainJsonObject(config?.legend) && config.legend.disable === true;
  const channels = [
    ...getLegendChannels(spec.encoding),
    ...(Array.isArray(spec.layer)
      ? spec.layer.flatMap((layer) =>
          isPlainJsonObject(layer) ? getLegendChannels(layer.encoding) : [],
        )
      : []),
  ];
  const resolve = isPlainJsonObject(spec.resolve) ? spec.resolve : undefined;
  const isShared = (channel: string) =>
    ['legend', 'scale'].every((kind) => {
      const resolution = resolve?.[kind];
      return !isPlainJsonObject(resolution) || resolution[channel] !== 'independent';
    });
  const disabledSharedChannels = new Set(
    channels
      .filter(({ channel, definition }) => isLegendDisabled(definition) && isShared(channel))
      .map(({ channel }) => channel),
  );
  const disabledSharedFields = new Set(
    channels.flatMap(({ channel, definition }) =>
      disabledSharedChannels.has(channel) && typeof definition.field === 'string'
        ? [definition.field]
        : [],
    ),
  );

  channels.forEach(({ channel, definition, encoding }) => {
    // A layout object explicitly enables a legend, even when a merged sibling disabled it.
    const disabledSibling =
      disabledSharedChannels.has(channel) ||
      (typeof definition.field === 'string' &&
        ((isShared(channel) && disabledSharedFields.has(definition.field)) ||
          channels.some(
            (other) =>
              other.encoding === encoding &&
              other.definition.field === definition.field &&
              isLegendDisabled(other.definition),
          )));

    if (
      isLegendDisabled(definition) ||
      (definition.legend === undefined && (disabledByDefault || disabledSibling))
    ) {
      return;
    }

    visit(definition);
  });
}

function hasAuthoredLegendLayout(definition: JsonObject): boolean {
  const legend = definition.legend;

  return (
    isPlainJsonObject(legend) &&
    VEGA_LITE_LEGEND_LAYOUT_KEYS.some((key) => legend[key] !== undefined)
  );
}

function moveLegendToRightColumn(definition: JsonObject) {
  const legend = isPlainJsonObject(definition.legend) ? definition.legend : {};

  definition.legend = { ...legend, columns: 1, direction: 'vertical', orient: 'right' };
}

function createRightLegendPadding(padding: JsonValue | undefined): JsonObject {
  if (typeof padding === 'number') {
    return {
      bottom: padding,
      left: padding,
      right: padding + VEGA_LITE_RIGHT_LEGEND_PADDING_PX,
      top: padding,
    };
  }

  if (isPlainJsonObject(padding)) {
    const right = typeof padding.right === 'number' ? padding.right : 0;

    return { ...padding, right: right + VEGA_LITE_RIGHT_LEGEND_PADDING_PX };
  }

  return { right: VEGA_LITE_RIGHT_LEGEND_PADDING_PX };
}

/**
 * Places legends that the widget viewport would otherwise cut off. Vega stacks bottom legends under
 * the plot and lets them grow past the host, so several of them push the plot out of the exported
 * image. Two cases need the runtime to step in, and nothing else does:
 *
 * - a viewport too narrow for a bottom legend, where every legend moves to the right column so it
 *   stays reachable even if the definition asked for another placement;
 * - a wide viewport with more than one legend that the definition left unplaced, where only those
 *   automatic legends move. An authored `orient`, `direction`, `columns`, `legendX` or `legendY` is
 *   a deliberate layout and is kept, so a definition can still lay out tall or wide legends itself.
 */
function applyResponsiveLegendLayout(spec: JsonObject, dimensions: VegaLiteChartDimensions) {
  let legendCount = 0;
  let automaticLegendCount = 0;

  forEachSpecLegendChannel(spec, (definition) => {
    legendCount += 1;

    if (!hasAuthoredLegendLayout(definition)) {
      automaticLegendCount += 1;
    }
  });

  if (legendCount === 0) {
    return;
  }

  const isNarrowViewport = dimensions.width < VEGA_LITE_NARROW_VIEWPORT_PX;

  if (!isNarrowViewport && automaticLegendCount < 2) {
    return;
  }

  forEachSpecLegendChannel(spec, (definition) => {
    if (isNarrowViewport || !hasAuthoredLegendLayout(definition)) {
      moveLegendToRightColumn(definition);
    }
  });

  spec.padding = createRightLegendPadding(spec.padding);
}

/**
 * Keeps the rendered canvas inside the measured host. Vega's width and height describe the plot
 * area, while axes and bottom legends are added outside it, so the runtime-only plot height also
 * reserves space for that chrome.
 */
export function getVegaLiteRuntimeDimensions(
  dimensions: VegaLiteChartDimensions,
): VegaLiteChartDimensions {
  const padding = VEGA_LITE_VIEWPORT_PADDING_PX * 2;
  const availableHeight = Math.max(1, Math.floor(dimensions.height) - padding);
  const verticalChromeReserve = Math.min(
    VEGA_LITE_VERTICAL_CHROME_RESERVE_PX,
    Math.floor(availableHeight * VEGA_LITE_MAX_VERTICAL_CHROME_RATIO),
  );

  return {
    height: Math.max(1, availableHeight - verticalChromeReserve),
    width: Math.max(1, Math.floor(dimensions.width) - padding),
  };
}

/**
 * Produces the runtime-only definition the renderer embeds. Host styling stays underneath the
 * authored config, while numeric host dimensions and fit autosizing replace authored sizing so
 * discrete step scales cannot ignore the widget viewport. The clone is also bound to the Neptune
 * dataset: the policy accepts a definition that omits "data", and only the runtime copy carries the
 * binding and the bin step safety floor, so a persisted definition is never rewritten on its way
 * to the renderer.
 */
export function prepareVegaLiteRenderSpec(
  spec: JsonObject,
  style: VegaLiteRenderStyle,
  dimensions: VegaLiteChartDimensions,
): JsonObject {
  const result = cloneJsonObject(spec);
  const specConfig = isPlainJsonObject(result.config) ? result.config : undefined;
  const styleConfig = createStyleConfig(style);
  const config: JsonObject = { ...specConfig };
  const runtimeDimensions = getVegaLiteRuntimeDimensions(dimensions);

  Object.entries(styleConfig).forEach(([group, styleGroup]) => {
    config[group] = mergeConfigGroup(styleGroup, specConfig?.[group]);
  });

  if (config.background === undefined) {
    config.background = style.background;
  }

  result.config = config;
  applyRuntimeBinStepFloor(result);
  applyResponsiveLegendLayout(result, dimensions);
  result.data = { name: VEGA_LITE_DATASET_NAME };
  result.autosize = { contains: 'padding', resize: true, type: 'fit' };
  result.height = runtimeDimensions.height;
  result.width = runtimeDimensions.width;

  return result;
}
